/**
 * @prop --padding-top: Padding Top of scroll container
 * @prop --padding-bottom: Padding Bottom of scroll container
 * @prop --peek-in: Peek size (inside container) showing the next/previous slides
 * @prop --peek-out: Negative peek size (breakout of container) showing the next/previous slides
 * @prop --gutter: Space between the slides
 * @prop --snap-stop: Snap stop of scroll container
 */

:host {
  @include spaceCurve('--gutter', 'regular');
  --peek: calc(var(--peek-in, 0px) + var(--peek-out, 0px));
  --peek-in: calc(#{space('medium')} + #{spaceCurveValue('regular')});
  // --peek-out: #{space('medium')};
  --padding-top: 0px;
  --padding-bottom: 0px;
  --snap-stop: always;
  --slides: 1;
  --slide-size: calc((100% - (var(--slides) - 1) * var(--gutter)) / var(--slides));

  @include mq($from: small) {
    --peek-in: calc(#{space('xxxlarge')} + #{spaceCurveValue('regular', 'medium')});
    // --peek-out: #{$containerMarginSmall};
  }

  @include mq($from: medium) {
    --peek-in: calc(#{space('big')} + #{spaceCurveValue('regular', 'large')});
    // --peek-out: 0px;
  }

  ::slotted(*) {
    width: 100%;
    height: inherit;
    flex-shrink: 0;
    scroll-snap-align: start;
    scroll-snap-stop: var(--snap-stop);
    transition: opacity $baseTransitionAnimationSpeed;

    [inert] {
      opacity: 0.5;
    }

    &:where(stzh-figure[inert]) {
      --caption-opacity: 0;
    }

    img {
      -webkit-user-drag: none;
      user-drag: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -webkit-user-select: none;
      user-select: none;
    }
  }

  ::slotted([slot="actions"]) {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    pointer-events: none;
    padding: 0 var(--peek);

    @media print {
      display: none;
    }

    stzh-text {
      width: auto;
    }

    stzh-button {
      pointer-events: all;
    }

    .stzh-carousel-action-previous,
    .stzh-carousel-action-next {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
    }

    .stzh-carousel-action-previous {
      left: calc((#{space('medium')} + #{spaceCurveValue('regular')}) / -1);

      @include mq($from: small) {
        left: calc((#{space('xxxlarge')} + #{spaceCurveValue('regular', 'medium')}) / -1);
      }

      @include mq($from: medium) {
        left: calc((#{space('big')} + #{spaceCurveValue('regular', 'large')}) / -1);
      }
    }

    .stzh-carousel-action-next {
      right: calc((#{space('medium')} + #{spaceCurveValue('regular')}) / -1);

      @include mq($from: small) {
        right: calc((#{space('xxxlarge')} + #{spaceCurveValue('regular', 'medium')}) / -1);
      }

      @include mq($from: medium) {
        right: calc((#{space('big')} + #{spaceCurveValue('regular', 'large')}) / -1);
      }
    }
  }

  ::slotted(stzh-ratio[slot="actions"]) {
    stzh-space,
    stzh-hspace,
    stzh-vspace {
      padding: space('xsmall');
    }
  }

  ::slotted(stzh-ratio[slot="actions"]) {
    --background-color: transparent;
  }

  /* Card */

  &[variant="cards"] {
    @include spaceCurve('--gutter', 'tiny');
    --padding-top: 4px;
    --padding-bottom: 4px;

    @include mq($from: medium) {
      --peek-out: #{$containerMarginMedium};
    }

    @include mq($from: large) {
      --peek-out: #{$containerMarginLarge};
    }

    @include mq($from: ultra) {
      --peek-out: calc((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2);
    }
  }

  &[variant="cards"] ::slotted([slot="actions"]) {
    position: relative;
    order: 1;
    margin-top: calc(#{space('medium')} - var(--padding-bottom));
  }

  /* No peek */

  &[no-peek]:not([no-peek="false"]) {
    --peek-out: 0px;
    --peek-in: 0px;
  }
}

.stzh-carousel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: calc(100% + var(--peek-out) * 2);
  margin-left: calc(var(--peek-out) / -1);
  margin-right: calc(var(--peek-out) / -1);

  &__vhidden {
    @include visuallyhidden;
  }

  &__indicator {
    @include spaceCurve('margin-top', 'regular');
    margin-left: auto;
    margin-right: auto;
    position: relative;
    width: 230px;
    height: 4px;
    background-color: $colorBlack20op;
  }

  &__indicator-track {
    position: absolute;
    background-color: $colorSecondary50;
    height: 6px;
    left: 0px;
    bottom: 0px;
    transition-property: left, width;
    transition-duration: $baseTransitionAnimationSpeed;
  }

  &__scroll {
    position: relative;
    grid-area: slides;
    display: grid;
    width: 100%;
    height: 100%;
    place-items: center;
    overflow-x: auto;
    overflow-y: visible;
    grid-auto-flow: column;
    grid-auto-columns: var(--slide-size);
    grid-auto-rows: 100%;
    column-gap: var(--gutter);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 var(--peek);
    padding: var(--padding-top) var(--peek) var(--padding-bottom);

    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &--is-draggable &__scroll {
    cursor: grab;
  }

  &--is-dragging &__scroll {
    cursor: grabbing;
  }

  &--is-disabled &__scroll {
    cursor: not-allowed;
    overflow: hidden;
  }

  &--is-non-interactive {
    pointer-events: none;
    touch-action: none;
  }
}
